import React, { useState, useEffect } from "react";
import { GetSystemConfigurations } from "../../services/GeneralMaintenanceService";
import CommonLoader from "../common/CommonLoader";
import {
  Button,
  Paper,
  Typography,
  Divider,
  Grid,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
  Container
} from "@mui/material";
import {
  Edit as EditIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  AccessTime as AccessTimeIcon
} from "@mui/icons-material";
import HTMLViewerEditor from "../../../components/common/HTMLViewerEditor";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../../../common-utilities/date-utility";

const SystemConfigurations = () => {
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [systemConfigurations, setSystemConfigurations] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [emailType, setEmailType] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    const fetchSystemConfigurations = async () => {
      try {
        setLoading(true);
        const response = await GetSystemConfigurations();
        setSystemConfigurations(response.results);
      } catch (error) {
        console.error("Error fetching system configurations:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemConfigurations();
  }, []);

  const handleEditClick = () => {
    navigate(`/vlp/edit-system-configuration`);
  };
  const handleDetailClick = (title, message, newEmailType) => {
    setDialogTitle(title);
    setEmailType(newEmailType);
    setDialogMessage(message);
    setIsDialogOpen(true);
    // renderDialog(title, message)
  };

  const handleCheckoutTimeClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <Container maxWidth="xl" className="vlp-component-container">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
          System Configurations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your system settings and configurations
        </Typography>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {loading && <CommonLoader loading={loading} />}

      {systemConfigurations && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
            size="large"
            sx={{
              mb: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'medium'
            }}
          >
            Edit Configuration
          </Button>

          <Grid container spacing={3}>
            {/* Company Information */}
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                  avatar={<BusinessIcon color="primary" />}
                  title="Company Information"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Company Name
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyName}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Address 1
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyAddress1}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Address 2
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyAddress2}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          City
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyCity}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          State
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyState}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Phone 1
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyPhone1}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Phone 2
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyPhone2}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Email 1
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyEmail1}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Email 2
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.companyEmail2}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Sales Contact
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.salesContact}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Personnel */}
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                <CardHeader
                  avatar={<PeopleIcon color="primary" />}
                  title="Personnel"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Office Manager
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.officeManager}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Lab Manager
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.labManager}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          QC Manager
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.qcmanager}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* SMTP Settings */}
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                <CardHeader
                  avatar={<EmailIcon color="primary" />}
                  title="SMTP Configuration"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          SMTP Address
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.smtpaddress}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          SMTP Account
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.smtpaccount}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          SMTP Password
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.smtppassword}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* File Paths & Settings */}
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                  avatar={<SettingsIcon color="primary" />}
                  title="File Paths & System Settings"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Attachment File Path
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>
                          {systemConfigurations.attachmentFilePath}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          HTML File Path
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>
                          {systemConfigurations.htmlfilePath}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Email ETK Updates
                        </Typography>
                        <Chip
                          label={systemConfigurations.emailEtkUpdates ? "Enabled" : "Disabled"}
                          color={systemConfigurations.emailEtkUpdates ? "success" : "default"}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          System ID
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.id}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Email Templates */}
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                  avatar={<EmailIcon color="primary" />}
                  title="Email Templates"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={3}>
                    {/* Lab Report Email */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'primary.50', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          Lab Report Email
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">Sender:</Typography>
                          <Typography variant="body2">{systemConfigurations.labReportEmailSender}</Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">Subject:</Typography>
                          <Typography variant="body2">{systemConfigurations.labReportEmailSubject}</Typography>
                        </Box>
                        <Button
                          startIcon={<VisibilityIcon />}
                          onClick={() =>
                            handleDetailClick(
                              "Lab Report Email Body",
                              systemConfigurations.labReportEmailBody,
                              "lab-report-email"
                            )
                          }
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none' }}
                        >
                          View Email Body
                        </Button>
                      </Paper>
                    </Grid>

                    {/* Billing Report Email */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'secondary.50', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          Billing Report Email
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">Sender:</Typography>
                          <Typography variant="body2">{systemConfigurations.billingReportEmailSender}</Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">Subject:</Typography>
                          <Typography variant="body2">{systemConfigurations.billingReportEmailSubject}</Typography>
                        </Box>
                        <Button
                          startIcon={<VisibilityIcon />}
                          onClick={() =>
                            handleDetailClick(
                              "Billing Report Email Body",
                              systemConfigurations.billingReportEmailBody,
                              "billing-report-email"
                            )
                          }
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none' }}
                        >
                          View Email Body
                        </Button>
                      </Paper>
                    </Grid>

                    {/* ETK Report Email */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'success.50', borderLeft: '4px solid', borderColor: 'success.main' }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                          ETK Report Email
                        </Typography>
                        <Button
                          startIcon={<VisibilityIcon />}
                          onClick={() =>
                            handleDetailClick(
                              "Etk Report Email Body",
                              systemConfigurations.etkReportEmailBody,
                              "etk-report-email"
                            )
                          }
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none' }}
                        >
                          View Email Body
                        </Button>
                      </Paper>
                    </Grid>

                    {/* Bottle Order Report Email */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'warning.50', borderLeft: '4px solid', borderColor: 'warning.main' }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                          Bottle Order Report Email
                        </Typography>
                        <Button
                          startIcon={<VisibilityIcon />}
                          onClick={() =>
                            handleDetailClick(
                              "Bottle Order Report Email Body",
                              systemConfigurations.bottleOrderReportEmailBody,
                              "bottle-order-report-email"
                            )
                          }
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none' }}
                        >
                          View Email Body
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Integration Settings */}
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                  avatar={<SecurityIcon color="primary" />}
                  title="Integration Settings"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          CMDP Username
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.cmdpUsername}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          CMDP Password
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.cmdpPass}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          CMDP Email
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.cmdpEmail}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            QBO Token
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {systemConfigurations.qboToken ? 'Token configured' : 'Not set'}
                          </Typography>
                        </Box>
                        <Tooltip title="View QBO Token">
                          <IconButton
                            onClick={() =>
                              handleDetailClick(
                                "Qbo Token",
                                systemConfigurations.qboToken
                              )
                            }
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Infusion Soft Token
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {systemConfigurations.infusionSoftToken ? 'Token configured' : 'Not set'}
                          </Typography>
                        </Box>
                        <Tooltip title="View Infusion Soft Token">
                          <IconButton
                            onClick={() =>
                              handleDetailClick(
                                "Infusion Soft Token",
                                systemConfigurations.infusionSoftToken
                              )
                            }
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon color="info" fontSize="small" />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Last QBO Payment Sync
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDateTime(systemConfigurations.lastQboPaymentSync)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Quote Comments */}
              <Grid item xs={12}>
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <CardHeader
                    avatar={<EmailIcon color="primary" />}
                    title="Quote Comments"
                    titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <Grid container spacing={3}>

                      {/* Generic Quote Comment */}
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                            Quote — Generic Comment
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {systemConfigurations.quoteGenericComment}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Wastewater Quote Comment */}
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                            Quote — Wastewater Comment
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {systemConfigurations.quoteWasteWaterComment}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Drinking Water Quote Comment */}
                      <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                            Quote — Drinking Water Comment
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {systemConfigurations.quoteDrinkingWaterComment}
                          </Typography>
                        </Paper>
                      </Grid>

                    </Grid>
                  </CardContent>
                </Card>
              </Grid>



            </Grid>

            {/* PFAS Email Settings */}
            <Grid item xs={12}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardHeader
                  avatar={<EmailIcon color="primary" />}
                  title="PFAS Email Settings"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    {/* From Email */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          From Email
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.pfasFromEmail || "-"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Email Subject */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Email Subject
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.pfasEmailSubject || "-"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Eurofins To Emails */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Eurofins To Emails
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.eurofinsToEmails || "-"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* CC Emails */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          CC Emails
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.pfasccEmails || "-"}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* ENB-584: daily inventory reorder summary */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Inventory Reorder From Email
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.inventoryReorderFromEmail || "-"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Inventory Reorder To Emails
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {systemConfigurations.inventoryReorderToEmails || "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

          </Grid>
        </Box>
      )}

      <HTMLViewerEditor
        isOpen={isDialogOpen}
        onClose={handleCheckoutTimeClose}
        title={dialogTitle}
        message={dialogMessage}
        emailType={emailType}
      />
    </Container>
  );
};

export default SystemConfigurations;
